/**
 * TemplateFormatToolbar.tsx
 * A floating toolbar shown while template edit mode is active. It applies
 * format overrides (bold, font size, alignment, color) and layout position adjustments
 * (nudge X/Y, reset position, delete block, restore deleted blocks) to the active
 * element inside the rendered template preview.
 */
import * as React from "react";
import {
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Trash2,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Type,
  ImagePlus,
  Move,
  MoveHorizontal,
  Hash,
  Undo2,
  Redo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  nudgeElementPosition,
  resetElementPosition,
  markElementDeleted,
  getDeletedElements,
  getElementPosition,
} from "@/utils/templateDrag";
import type { FieldFormattingConfig } from "@/utils/formatter";
import { TableContextToolbar } from "./TableContextToolbar";

interface Props {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onChanged?: () => void;
  /** Add a new free-form text line overlay to the active page. */
  onAddTextOverlay?: () => void;
  /** Add a new image overlay (base64) to the active page. */
  onAddImageOverlay?: () => void;
  /** Field display formats keyed by field name (number/currency/percent/date). */
  formats?: Record<string, FieldFormattingConfig>;
  /** Called when a field's display format changes. */
  onFormatsChange?: (formats: Record<string, FieldFormattingConfig>) => void;
  /** Undo/Redo availability + handlers (canvas snapshot history). */
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

/** Detect the field name inside a focused/selected element (`{{field}}` or `data-field-id`). */
function detectFieldName(el: HTMLElement | null): string | null {
  if (!el) return null;
  if (el.dataset.fieldId) return el.dataset.fieldId;
  if (el.dataset.fieldKey) return el.dataset.fieldKey;
  // Walk ancestors too — a field span may sit inside a selected paragraph.
  let cur: HTMLElement | null = el;
  while (cur) {
    if (cur.dataset.fieldId) return cur.dataset.fieldId;
    if (cur.dataset.fieldKey) return cur.dataset.fieldKey;
    const m = /{{\s*([^{}]+?)\s*}}/.exec(cur.textContent ?? "");
    if (m && m[1].trim() && !m[1].trim().startsWith("#")) return m[1].trim();
    cur = cur.parentElement;
  }
  return null;
}

export function TemplateFormatToolbar({ containerRef, onChanged, onAddTextOverlay, onAddImageOverlay, formats = {}, onFormatsChange, canUndo = false, canRedo = false, onUndo, onRedo }: Props) {
  const [selectedEl, setSelectedEl] = React.useState<HTMLElement | null>(null);
  const [deletedCount, setDeletedCount] = React.useState(0);
  const [deletedItems, setDeletedItems] = React.useState<HTMLElement[]>([]);

  const refreshState = React.useCallback(() => {
    if (!containerRef.current) return;
    const deleted = getDeletedElements(containerRef.current);
    setDeletedCount(deleted.length);
    setDeletedItems(deleted);
  }, [containerRef]);

  // Listen for click/focus in preview container to update selected element & deleted count
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handlePointerOrFocus = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const editable = target.closest("p, td, th, span, div, h1, h2, h3, h4, table") as HTMLElement | null;
      if (editable && container.contains(editable)) {
        setSelectedEl(editable);
      }
      refreshState();
    };

    container.addEventListener("click", handlePointerOrFocus);
    container.addEventListener("focusin", handlePointerOrFocus);
    refreshState();

    return () => {
      container.removeEventListener("click", handlePointerOrFocus);
      container.removeEventListener("focusin", handlePointerOrFocus);
    };
  }, [containerRef, refreshState]);

  const getFocusEl = (): HTMLElement | null => {
    const focused = containerRef.current?.querySelector(":focus") as HTMLElement | null;
    if (focused && focused.matches("p, td, th, span, div, h1, h2, h3, h4, table")) {
      return focused;
    }
    return selectedEl;
  };

  /** Finds the closest structural layout block (paragraph, table, content div). */
  const getBlockEl = (): HTMLElement | null => {
    const el = getFocusEl();
    if (!el) return null;
    const block = el.closest("p, table, div") as HTMLElement | null;
    return block ?? el;
  };

  const applyFormat = (mutate: (el: HTMLElement) => void) => {
    const el = getFocusEl();
    if (!el) return;
    mutate(el);
    el.setAttribute("data-format-edited", "true");
    onChanged?.();
    refreshState();
  };

  const currentSize = (): number => {
    const el = getFocusEl();
    if (!el) return 12;
    const px = parseFloat(window.getComputedStyle(el).fontSize);
    return Number.isFinite(px) ? px : 12;
  };

  const activeBlock = getBlockEl();
  const currentPos = activeBlock ? getElementPosition(activeBlock) : { x: 0, y: 0 };

  const handleDelete = () => {
    const block = getBlockEl();
    if (!block) return;
    markElementDeleted(block, true, () => {
      onChanged?.();
      setSelectedEl(null);
      refreshState();
    });
  };

  const handleRestore = (el?: HTMLElement) => {
    if (el) {
      markElementDeleted(el, false, () => {
        onChanged?.();
        refreshState();
      });
    } else {
      // Restore all
      deletedItems.forEach((item) => markElementDeleted(item, false));
      onChanged?.();
      refreshState();
    }
  };

  const handleNudge = (dx: number, dy: number) => {
    const block = getBlockEl();
    if (!block) return;
    nudgeElementPosition(block, dx, dy, () => {
      onChanged?.();
      refreshState();
    });
  };

  const handleResetPos = () => {
    const block = getBlockEl();
    if (!block) return;
    resetElementPosition(block, () => {
      onChanged?.();
      refreshState();
    });
  };

  const applyFieldFormat = (field: string, patch: FieldFormattingConfig) => {
    if (!onFormatsChange) return;
    onFormatsChange({ ...formats, [field]: { ...formats[field], ...patch } });
    onChanged?.();
  };

  const getElementSnippet = (el: HTMLElement): string => {
    const tag = el.tagName.toLowerCase();
    const text = (el.textContent || "").trim().slice(0, 20);
    return text ? `<${tag}> ${text}...` : `<${tag}>`;
  };

  return (
    <>
    <div className="flex flex-wrap items-center gap-1 rounded-lg border bg-card px-2 py-1 shadow-sm text-xs">
      {/* Undo / Redo */}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        title="Hoàn tác (Ctrl+Z)"
        disabled={!canUndo || !onUndo}
        onClick={onUndo}
      >
        <Undo2 className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        title="Làm lại (Ctrl+Y)"
        disabled={!canRedo || !onRedo}
        onClick={onRedo}
      >
        <Redo2 className="h-3.5 w-3.5" />
      </Button>
      <Separator orientation="vertical" className="mx-0.5 h-4" />

      {/* Add text / image overlays */}
      {onAddTextOverlay && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 gap-1 text-primary border-primary/30"
          title="Thêm dòng văn bản mới vào trang (kéo để định vị)"
          onClick={onAddTextOverlay}
        >
          <Type className="h-3.5 w-3.5" /> Thêm văn bản
        </Button>
      )}
      {onAddImageOverlay && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 gap-1 text-primary border-primary/30"
          title="Chèn hình ảnh (logo, chữ ký…) — nhúng base64, giữ nguyên khi in/PDF"
          onClick={onAddImageOverlay}
        >
          <ImagePlus className="h-3.5 w-3.5" /> Chèn ảnh
        </Button>
      )}
      <Separator orientation="vertical" className="mx-0.5 h-4" />

      {/* Selected Element Badge */}
      <div className="flex items-center gap-1 pr-1 border-r">
        <span className="text-[10px] text-muted-foreground">Khối:</span>
        <Badge variant="outline" className="text-[10px] font-mono h-5 px-1.5 bg-muted/40">
          {activeBlock ? activeBlock.tagName.toLowerCase() : "chưa chọn"}
        </Badge>
      </div>

      {/* Text Format Tools */}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        title="In đậm"
        onClick={() =>
          applyFormat((el) => {
            el.style.fontWeight = el.style.fontWeight === "bold" ? "normal" : "bold";
          })
        }
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        title="In nghiêng"
        onClick={() =>
          applyFormat((el) => {
            el.style.fontStyle = el.style.fontStyle === "italic" ? "normal" : "italic";
          })
        }
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>

      <Separator orientation="vertical" className="mx-0.5 h-4" />

      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-1.5 text-xs font-semibold"
        title="Tăng cỡ chữ"
        onClick={() =>
          applyFormat((el) => {
            el.style.fontSize = `${Math.min(48, currentSize() + 1)}px`;
          })
        }
      >
        A+
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-1.5 text-xs font-semibold"
        title="Giảm cỡ chữ"
        onClick={() =>
          applyFormat((el) => {
            el.style.fontSize = `${Math.max(6, currentSize() - 1)}px`;
          })
        }
      >
        A−
      </Button>

      <Separator orientation="vertical" className="mx-0.5 h-4" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 px-2 gap-1" title="Căn lề">
            <AlignLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline text-[10px]">Căn</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-auto">
          <DropdownMenuItem onClick={() => applyFormat((el) => { el.style.textAlign = "left"; })} className="text-xs gap-2">
            <AlignLeft className="h-3.5 w-3.5" /> Căn trái
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => applyFormat((el) => { el.style.textAlign = "center"; })} className="text-xs gap-2">
            <AlignCenter className="h-3.5 w-3.5" /> Căn giữa
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => applyFormat((el) => { el.style.textAlign = "right"; })} className="text-xs gap-2">
            <AlignRight className="h-3.5 w-3.5" /> Căn phải
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Align whole block within the page (left / center / right) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 px-2 gap-1" title="Canh khối trong trang">
            <MoveHorizontal className="h-3.5 w-3.5" />
            <span className="hidden sm:inline text-[10px]">Canh khối</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-auto">
          <DropdownMenuItem
            onClick={() =>
              applyFormat((el) => {
                el.style.display = "block";
                el.style.width = "fit-content";
                el.style.marginLeft = "0";
                el.style.marginRight = "auto";
              })
            }
            className="text-xs gap-2"
          >
            <AlignLeft className="h-3.5 w-3.5" /> Khối bên trái
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              applyFormat((el) => {
                el.style.display = "block";
                el.style.width = "fit-content";
                el.style.marginLeft = "auto";
                el.style.marginRight = "auto";
              })
            }
            className="text-xs gap-2"
          >
            <AlignCenter className="h-3.5 w-3.5" /> Khối giữa trang
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              applyFormat((el) => {
                el.style.display = "block";
                el.style.width = "fit-content";
                el.style.marginLeft = "auto";
                el.style.marginRight = "0";
              })
            }
            className="text-xs gap-2"
          >
            <AlignRight className="h-3.5 w-3.5" /> Khối bên phải
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Number format for the selected field placeholder */}
      {(() => {
        const field = detectFieldName(getFocusEl());
        if (!field || !onFormatsChange) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="h-7 px-2 gap-1" title={`Format số cho trường "${field}"`}>
                <Hash className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-[10px]">Số</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-auto">
              <DropdownMenuLabel className="text-[10px] text-muted-foreground">
                Format số · {field}
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => applyFieldFormat(field, { formatType: "number", decimalPlaces: 0 })} className="text-xs gap-2">
                Số nguyên (0, 1, 2…)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => applyFieldFormat(field, { formatType: "number", decimalPlaces: 2 })} className="text-xs gap-2">
                Số thập phân (1.234,56)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => applyFieldFormat(field, { formatType: "currency", decimalPlaces: 2, currencySymbol: "₫" })} className="text-xs gap-2">
                Tiền tệ ₫
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => applyFieldFormat(field, { formatType: "percent", decimalPlaces: 1 })} className="text-xs gap-2">
                Phần trăm %
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => applyFieldFormat(field, { formatType: "text" })} className="text-xs gap-2">
                Mặc định (text)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })()}

      <Separator orientation="vertical" className="mx-0.5 h-4" />

      <label className="flex items-center gap-1 px-1 text-[10px] text-muted-foreground" title="Màu chữ">
        <span className="hidden sm:inline">Màu</span>
        <input
          type="color"
          className="h-5 w-6 cursor-pointer rounded border p-0 bg-transparent"
          onChange={(e) =>
            applyFormat((el) => {
              el.style.color = e.target.value;
            })
          }
        />
      </label>

      <Separator orientation="vertical" className="mx-1 h-4" />

      {/* Position Nudge Controls */}
      <div className="flex items-center gap-0.5 bg-muted/40 p-0.5 rounded border">
        <span className="text-[10px] font-mono px-1 text-muted-foreground" title="Tọa độ offset X/Y">
          X:{Math.round(currentPos.x)} Y:{Math.round(currentPos.y)}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] gap-1" disabled={!activeBlock} title="Dời vị trí">
              <Move className="h-3 w-3" /> Dời
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-auto">
            <DropdownMenuItem onClick={() => handleNudge(-5, 0)} className="text-xs gap-2">
              <ArrowLeft className="h-3 w-3" /> Sang trái
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNudge(0, -5)} className="text-xs gap-2">
              <ArrowUp className="h-3 w-3" /> Lên trên
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNudge(0, 5)} className="text-xs gap-2">
              <ArrowDown className="h-3 w-3" /> Xuống dưới
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNudge(5, 0)} className="text-xs gap-2">
              <ArrowRight className="h-3 w-3" /> Sang phải
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleResetPos} disabled={!activeBlock || (currentPos.x === 0 && currentPos.y === 0)} className="text-xs gap-2">
              <RotateCcw className="h-3 w-3" /> Đặt lại vị trí
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Separator orientation="vertical" className="mx-1 h-4" />

      {/* Component Deletion & Restoration */}
      <Button
        size="sm"
        variant="destructive"
        className="h-7 px-2 text-xs gap-1"
        title="Xóa/Ẩn khối component này khỏi layout template"
        disabled={!activeBlock}
        onClick={handleDelete}
      >
        <Trash2 className="h-3.5 w-3.5" /> Xóa khối
      </Button>

      {deletedCount > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1 text-amber-600 border-amber-300 bg-amber-50">
              <Undo2 className="h-3.5 w-3.5" /> Đã xóa ({deletedCount})
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs">Khôi phục thành phần đã xóa</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleRestore()} className="text-xs font-semibold text-primary">
              <RotateCcw className="h-3.5 w-3.5 mr-2" /> Khôi phục tất cả ({deletedCount})
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {deletedItems.map((item, idx) => (
              <DropdownMenuItem key={idx} onClick={() => handleRestore(item)} className="text-xs flex items-center justify-between">
                <span className="truncate">{getElementSnippet(item)}</span>
                <Badge variant="outline" className="ml-1 text-[9px]">Phục hồi</Badge>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>

    {/* Table Context Toolbar — auto-shows when a table cell is focused */}
    <TableContextToolbar containerRef={containerRef} onChanged={onChanged} />
    </>
  );
}